import {
  buildTargetUrl,
  hasUploadFailed,
  isStoryAbandoned,
  isStoryLoading,
  isStoryReady,
} from '@src/components/Dashboard/utils';
import { speakerPromptPendingMessage } from '@src/hooks/useSpeakerIdentificationPrompt';
import {
  useDashboard,
  useUserIdentity,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { ContentTab, Studio } from '@src/types.ts/general';
import { isAudioThenVideoEnabled } from '@src/utility/audioThenVideoFlag';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import NewStoriesQuerySubscriptionComponent, {
  type NewStoryUpdate,
} from './NewStoriesQuerySubscriptionComponent';
import UploadProgressToast from './UploadProgressToast';

import type { ToastAction, ToastRequest } from '@src/stores/toast';

const logger = LogManager.getLogger('component:UploadCompletionToaster');

/**
 * Global subscription + reaction that fires a "your video is ready" toast
 * regardless of which page the user is on. Mounted in RootLayout so it stays
 * alive across route changes.
 *
 * Skips mounting the subscription on /story-manager where the Dashboard
 * already owns an identical (superset) subscription via
 * NewStoriesQuerySubscriptionComponent. This prevents duplicated network
 * work and race conditions between the two writers.
 *
 * Only fires for stories that we have *previously observed as not-ready* and
 * have now flipped to ready. Stories that show up in the dashboard already in
 * the ready state (e.g., loaded fresh from Dato on app boot) are seeded into
 * the dedupe set silently — no toast.
 */
const UploadCompletionToaster = observer(() => {
  const dashboardStore = useDashboard();
  const userStoryUploadFlowStore = useUserStoryUploadFlow();
  const videoCreator = useVideoCreatorStore();
  const location = useLocation();
  const navigate = useNavigate();

  const isOnDashboard = location.pathname === `/${Studio.dashboard}`;

  // Route navigation through refs so opening a story from the toast doesn't
  // re-run the reaction effect (which would lose its `previous` snapshot and
  // re-seed the dedupe set). Updated every render, read lazily on click.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const searchRef = useRef(location.search);
  searchRef.current = location.search;
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const userIdentity = useUserIdentity();
  const { enableSpeakersIdentification, enableTranslationReviewerMode } =
    useFlagsCombination(userIdentity.currentRole);
  const speakerPromptEnabledRef = useRef(false);
  speakerPromptEnabledRef.current =
    enableSpeakersIdentification && !enableTranslationReviewerMode;

  // Session uploads still in flight — the stories whose Story Manager row shows
  // a progress bar. Away from that page the same progress rides in a toast
  // (UploadProgressToast), so leaving mid-upload never loses sight of it.
  // Stories uploaded elsewhere (another tab, another user) have no phase
  // record here and get only the "is ready" toast below.
  const progressStories = dashboardStore.allStories.filter(
    s =>
      isStoryLoading(s) &&
      !isStoryAbandoned(s) &&
      !hasUploadFailed(s) &&
      userStoryUploadFlowStore.getStoryUploadPhase(s.id).phase !== 'idle',
  );
  // storyId → its live progress toast. The ready reaction below swaps that
  // toast for the outcome in place rather than stacking a second one.
  const progressToastIdsRef = useRef(new Map<string, number>());

  const incompleteStoryIds = dashboardStore.allStories
    .filter(isStoryLoading)
    .map(s => s.id);

  // Stabilize the subscription: only re-mount (and only update the watched id
  // list) when a previously-unknown loading story appears (set grows).
  // Stories transitioning out of loading must NOT tear down the subscription —
  // that's the exact window where the ready transition we care about could be
  // missed. We accumulate ids in a ref and bump an epoch counter only when
  // the set grows; the rendered `storyIds` prop is derived from the same
  // snapshot so the underlying query variables stay stable too.
  const watchedIdsRef = useRef<string[]>([]);
  const [subscriptionEpoch, setSubscriptionEpoch] = useState(0);
  useEffect(() => {
    const known = new Set(watchedIdsRef.current);
    const added: string[] = [];
    for (const id of incompleteStoryIds) {
      if (!known.has(id)) {
        known.add(id);
        added.push(id);
      }
    }
    if (added.length > 0) {
      watchedIdsRef.current = [...watchedIdsRef.current, ...added];
      setSubscriptionEpoch(n => n + 1);
    }
  }, [incompleteStoryIds]);

  // `useMemo` keyed on the epoch ensures the storyIds array reference is
  // stable between grows, even though `watchedIdsRef.current` is mutated.
  const watchedStoryIds = useMemo(
    () => watchedIdsRef.current.slice(),
    [subscriptionEpoch],
  );

  const handleUpdates = (updates: NewStoryUpdate[]) => {
    for (const update of updates) {
      const existing = dashboardStore.allStories.find(s => s.id === update.id);
      if (!existing) {
        logger.debug('dropped update for unknown story', {
          storyId: update.id,
        });
        continue;
      }
      // Original video isn't returned for AWS uploads — keep the existing
      // value rather than overwriting with undefined. For non-AWS, the field
      // arrives as `datoHostedVideo`. Decide based on the post-merge
      // `useAws` value, since placeholder stories seed `useAws: true` and
      // would otherwise mask a real non-AWS update.
      const mergedUseAws = update.useAws ?? existing.useAws;
      const originalVideo = mergedUseAws
        ? existing.originalVideo
        : update.datoHostedVideo;
      dashboardStore.updateStory({
        ...existing,
        ...update,
        originalVideo,
        isNew: true,
      });
    }
  };

  useEffect(() => {
    const dispose = reaction(
      () =>
        dashboardStore.allStories.map(s => ({
          id: s.id,
          title: s.title,
          ready: isStoryReady(s),
        })),
      (current, previous) => {
        const prevReady = previous
          ? new Map(previous.map(s => [s.id, s.ready]))
          : null;

        // Seed: any story we're seeing for the first time gets added to the
        // dedupe set if it's already ready. This handles both the very first
        // observation (previous = undefined) and the case where stories are
        // pushed into allStories after the reaction starts firing (Dato
        // initial load arriving after `fireImmediately`).
        for (const s of current) {
          if (s.ready && (!prevReady || !prevReady.has(s.id))) {
            userStoryUploadFlowStore.markReadyToastShown(s.id);
          }
        }

        if (!prevReady) return;

        // Only true loading→ready transitions: must have been observed
        // previously AND not-ready at that time.
        const newlyReady = current.filter(
          s =>
            s.ready &&
            prevReady.has(s.id) &&
            !prevReady.get(s.id) &&
            !userStoryUploadFlowStore.toastedReadyStoryIds.has(s.id),
        );
        if (newlyReady.length === 0) return;

        for (const s of newlyReady) {
          userStoryUploadFlowStore.markReadyToastShown(s.id);
          const progressToastId = progressToastIdsRef.current.get(s.id);
          progressToastIdsRef.current.delete(s.id);
          const title = s.title || 'Your video';

          // Only this session's own uploads (they carry a phase record) get
          // the speaker-identification prompt (SpeakerIdentificationPrompt).
          const promptsForSpeakers =
            speakerPromptEnabledRef.current &&
            userStoryUploadFlowStore.getStoryUploadPhase(s.id).phase !== 'idle';
          if (promptsForSpeakers) {
            userStoryUploadFlowStore.queueSpeakerPrompt(s.id);
            logger.info('Queued speaker identification prompt', {
              storyId: s.id,
            });
          }
          // `/` is Creator Studio too while homeEnableDefaultLanding is off
          // (DefaultLandingRoute); while it is on, `/` redirects to /home.
          const isViewingStory =
            videoCreator.story?.id === s.id &&
            (pathnameRef.current === '/' ||
              pathnameRef.current === `/${Studio.content}` ||
              pathnameRef.current === `/${Studio.creator}`);
          // Already in the story and the prompt already came and went (it can
          // open during Versioning): no "Open" — they are in it.
          if (
            promptsForSpeakers &&
            isViewingStory &&
            userStoryUploadFlowStore.speakerPromptState[s.id] === 'done'
          ) {
            const plainReady: ToastRequest = {
              state: 'success',
              message: `${title} is ready`,
              delay: 5000,
            };
            if (progressToastId !== undefined) {
              videoCreator.updateToast(progressToastId, plainReady);
            } else {
              videoCreator.showToast(plainReady);
            }
            continue;
          }
          // Already in the story, prompt still to come: no "is ready — Open" —
          // the progress toast becomes the speaker prompt's pending toast,
          // which the prompt ends (modal opens, or "is ready" when there is
          // nothing to identify).
          if (promptsForSpeakers && isViewingStory) {
            const pendingToast: ToastRequest = {
              state: 'pending',
              message: speakerPromptPendingMessage(title),
            };
            const toastId =
              progressToastId !== undefined
                ? videoCreator.updateToast(progressToastId, pendingToast)
                : videoCreator.showToast(pendingToast);
            userStoryUploadFlowStore.holdSpeakerPromptToast(
              s.id,
              toastId,
              title,
            );
            continue;
          }

          // PLAT-12104: no "Open" while the browser's scene/face detection
          // still holds the story closed — its own toast is up meanwhile.
          const analyzingVideo =
            userStoryUploadFlowStore.getClientVideoAnalysisProgress(s.id) !==
            undefined;
          const openAction: ToastAction = {
            label: 'Open',
            onClick: () => {
              // PLAT-11934 — a fresh story opens on its transcript, not an
              // empty Video Clips grid.
              navigateRef.current(
                `/${Studio.content}${buildTargetUrl(
                  new URLSearchParams(searchRef.current),
                  s.id,
                  undefined,
                  ContentTab.transcript_speakers,
                )}`,
              );
            },
          };
          const readyToast: ToastRequest = {
            state: 'success',
            message: `${title} is ready`,
            delay: 5000,
            ...(analyzingVideo ? {} : { action: openAction }),
          };
          // Finished while its progress toast is up: turn that toast into the
          // outcome (same row, 5 s) instead of stacking a second one.
          if (progressToastId !== undefined) {
            videoCreator.updateToast(progressToastId, readyToast);
          } else {
            videoCreator.showToast(readyToast);
          }
        }
      },
      { fireImmediately: true },
    );
    return dispose;
  }, [dashboardStore, userStoryUploadFlowStore, videoCreator]);

  // On Story Manager the rows already show each upload's bar; the toasts come
  // up as soon as the user navigates away and go down when they come back.
  // Kill switch (PLAT-11222): the toast only exists because audio-first sends
  // the user into the editor while the upload is still processing. With the
  // switch off there is nowhere to be but Story Manager until the story is
  // ready, and that page already carries the bar on the row.
  const progressToasts =
    isOnDashboard || !isAudioThenVideoEnabled()
      ? null
      : progressStories.map(story => (
          <UploadProgressToast
            key={story.id}
            story={story}
            progressToastIds={progressToastIdsRef}
          />
        ));

  const showSubscription =
    incompleteStoryIds.length > 0 &&
    !isOnDashboard &&
    watchedStoryIds.length > 0;

  return (
    <>
      {progressToasts}
      {showSubscription && (
        <NewStoriesQuerySubscriptionComponent
          key={subscriptionEpoch}
          storyIds={watchedStoryIds}
          onDataReceived={handleUpdates}
          onError={(error: unknown) => {
            logger.error(
              'Story update subscription failed',
              extractErrorInfo(error),
            );
          }}
        />
      )}
    </>
  );
});

export default UploadCompletionToaster;
